import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, UserCheck } from "lucide-react";
import { personsApi } from "@/api/counterparties";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import type { AuthorizedSigner, PersonListItemDto } from "@/types/counterparties";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";

// Deal Pipeline spec §3.2 — link/unlink Person records as
// authorized signers. The list is persisted as a JSON string on the
// Entity DTO (authorizedSignersJson) so the Update endpoint just takes
// the serialized array. Per spec, each entry carries Title and Scope.

interface AuthorizedSignersListProps {
  signers: AuthorizedSigner[];
  onChange: (next: AuthorizedSigner[]) => void;
  canEdit: boolean;
}

export function parseSignersJson(json: string | null | undefined): AuthorizedSigner[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((s): s is Record<string, unknown> => typeof s === "object" && s !== null)
      .map((s) => ({
        personId: String(s.personId ?? s.PersonId ?? ""),
        title: (s.title ?? s.Title ?? null) as string | null,
        scope: (s.scope ?? s.Scope ?? null) as string | null,
      }))
      .filter((s) => s.personId.length > 0);
  } catch {
    return [];
  }
}

export function serializeSigners(signers: AuthorizedSigner[]): string {
  return JSON.stringify(signers.map((s) => ({
    personId: s.personId,
    title: s.title ?? null,
    scope: s.scope ?? null,
  })));
}

export function AuthorizedSignersList({
  signers,
  onChange,
  canEdit,
}: AuthorizedSignersListProps) {
  const [adding, setAdding] = useState(false);

  const personIds = signers.map((s) => s.personId);

  // Resolve the linked persons' display names. We fetch a small page of
  // persons that match no filter — fine at today's scale; a later change may
  // batch-fetch by ID once that endpoint exists.
  const personsQuery = useQuery({
    queryKey: ["persons", "linked-signers", personIds],
    queryFn: () => personsApi.list({ pageSize: 200 }),
    enabled: signers.length > 0,
  });

  const personById = useMemo(() => {
    const map = new Map<string, PersonListItemDto>();
    for (const p of personsQuery.data?.data ?? []) map.set(p.id, p);
    return map;
  }, [personsQuery.data]);

  const remove = (personId: string) => {
    onChange(signers.filter((s) => s.personId !== personId));
  };

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-h3 text-onyx m-0 normal-case tracking-normal">
            Authorized signers
          </h3>
          <p className="text-caption text-gray-mid m-0">
            Person records authorized to sign on behalf of this entity. Persisted on the entity's
            authorizedSignersJson field.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setAdding(true)}>
            <Plus size={14} strokeWidth={2} aria-hidden /> Add signer
          </Button>
        )}
      </div>

      {signers.length === 0 ? (
        <div className="border border-dashed border-border-light rounded-md p-space-4 text-small text-gray-mid text-center">
          No authorized signers linked yet.
        </div>
      ) : (
        <ul className="border border-border-light rounded-md divide-y divide-border-light">
          {signers.map((s) => {
            const person = personById.get(s.personId);
            return (
              <li key={s.personId} className="flex items-center justify-between gap-space-3 p-space-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-small text-onyx truncate flex items-center gap-space-2">
                    <UserCheck size={14} strokeWidth={1.5} aria-hidden className="text-gold-shadow" />
                    {person?.displayName ?? `(Person ${s.personId.slice(0, 8)})`}
                  </div>
                  <div className="text-caption text-gray-mid">
                    {s.title || "—"}
                    {s.scope ? <> · <span className="italic">{s.scope}</span></> : null}
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => remove(s.personId)}
                    aria-label={`Remove signer ${person?.displayName ?? s.personId}`}
                    className="p-space-2 text-gray-mid hover:text-status-error rounded"
                  >
                    <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding && (
        <AddSignerModal
          existingIds={personIds}
          onCancel={() => setAdding(false)}
          onAdd={(s) => {
            onChange([...signers, s]);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

interface AddSignerModalProps {
  existingIds: string[];
  onAdd: (signer: AuthorizedSigner) => void;
  onCancel: () => void;
}

function AddSignerModal({ existingIds, onAdd, onCancel }: AddSignerModalProps) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<PersonListItemDto | null>(null);
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");

  const personsQuery = useQuery({
    queryKey: ["persons", "signer-search", search],
    queryFn: () => personsApi.list({ q: search || undefined, pageSize: 20 }),
  });

  const candidates = (personsQuery.data?.data ?? []).filter(
    (p) => !existingIds.includes(p.id)
  );

  return (
    <Modal
      open
      onClose={onCancel}
      dismissOnBackdropClick={false}
      title="Add authorized signer"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!picked}
            onClick={() => {
              if (!picked) return;
              onAdd({
                personId: picked.id,
                title: title.trim() || null,
                scope: scope.trim() || null,
              });
            }}
          >
            Link signer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Find person" required>
          <Input
            type="search"
            placeholder="Search by name, email, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search persons"
          />
        </FormField>
        <ul className="border border-border-light rounded-md max-h-48 overflow-y-auto divide-y divide-border-light">
          {candidates.length === 0 ? (
            <li className="p-space-3 text-caption text-gray-mid">No matching persons.</li>
          ) : (
            candidates.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setPicked(p)}
                  className={
                    "w-full text-left p-space-3 hover:bg-white-warm flex items-center justify-between" +
                    (picked?.id === p.id ? " bg-gold/10" : "")
                  }
                >
                  <div>
                    <div className="font-semibold text-small text-onyx">{p.displayName}</div>
                    <div className="text-caption text-gray-mid">
                      {displayEmail(p.primaryEmail) ??
                        (p.primaryEmail ? NO_EMAIL_ON_FILE : "—")}
                      {p.state ? ` · ${p.state}` : ""}
                    </div>
                  </div>
                  {picked?.id === p.id && (
                    <span className="text-caption uppercase tracking-wide text-gold-shadow">Selected</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
        <FormField label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Managing Member, President"
          />
        </FormField>
        <FormField label="Signing scope">
          <Input
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="e.g. All entity matters, banking only"
          />
        </FormField>
      </div>
    </Modal>
  );
}
